import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { isThisISOWeek, isThisYear, isToday, isYesterday } from 'date-fns';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CgBlock, CgUnblock } from 'react-icons/cg';
import { IoIosArrowBack, IoMdMore } from 'react-icons/io';
import {
  MdInfoOutline,
  MdMoreTime,
  MdOutlineNotificationsActive,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { formatPartnerRouteName } from '../../../common/formatter';
import { ROUTES } from '../../../constants/Constants';
import useActionModal from '../../../hooks/useActionModal';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useChatUserDetailsModal from '../../../hooks/useChatUserDetailsModal';
import usePushNotifications from '../../../hooks/usePushNotifications';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import {
  ConversationUserStatusType,
  type ConversationUserType,
} from '../../../proxies/ChatProxy';
import { ProfilePreferenceTypeEnum } from '../../../proxies/UserProxy';
import {
  blockConversationRequest,
  getRecentConversationsRequest,
  unblockConversationRequest,
} from '../../../redux/ducks/ChatDucks';
import type { RecentConversationDataType } from '../../../redux/ducks/ChatDucks.types';
import { setPushPreference } from '../../../redux/ducks/StorageDucks';
import { ActionModalActionType } from '../../../redux/ducks/UIDucks.types';
import { userUpdateProfilePreferenceRequest } from '../../../redux/ducks/UserDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import { onKeyUpDefault } from '../../inputs/Input';
import MainPopupMenu from '../../layout/Navbar/MainPopupMenu';
import MenuItem from '../../layout/Navbar/MenuItem';

export type ChatHeaderProps = {
  onBack: () => void;
};

const ChatHeader: React.FC<ChatHeaderProps> = ({ onBack }) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const chatUserDetailsModal = useChatUserDetailsModal();
  const actionModal = useActionModal();
  const [onBlockUser, setOnBlockUser] = useState<boolean>(false);
  const [onUnblockUser, setOnUnblockUser] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  const {
    isSupported,
    isPermitted,
    requestPermission: requestPushPermission,
  } = usePushNotifications();

  const conversations = useSelector(
    (state: RootReducerType) => state.chat.conversations,
  );

  const currentConversation = useSelector(
    (state: RootReducerType) => state.chat.currentConversation,
  );

  const { onActionModalConfirm } = useSelector(
    (state: RootReducerType) => state.ui,
  );

  const blockConversationResponse = useSelector(
    (state: RootReducerType) => state.chat.blockConversationResponse,
  );

  const unblockConversationResponse = useSelector(
    (state: RootReducerType) => state.chat.unblockConversationResponse,
  );

  const chatConversation = useMemo<
    RecentConversationDataType | undefined
  >(() => {
    if (currentConversation && conversations) {
      const conv = conversations.conversations.filter(
        (c) => c.data.id === currentConversation,
      );

      if (conv.length > 0) {
        return conv[0].data;
      }
    }

    return undefined;
  }, [currentConversation, conversations]);

  const targetConversation = useMemo<RecentConversationDataType | undefined>(
    () => chatConversation,
    [chatConversation],
  );

  const chatUser = useMemo<ConversationUserType | undefined>(() => {
    if (currentConversation && conversations) {
      const conv = conversations.conversations.filter(
        (c) => c.data.id === currentConversation,
      );

      if (conv.length > 0) {
        return conv[0].data.user;
      }
    }

    return undefined;
  }, [currentConversation]);

  const formatLastMessageLabel = useMemo(() => {
    if (chatUser?.last_time_online) {
      const messageDate = new Date(chatUser.last_time_online);

      let datePart = null;

      if (isToday(messageDate)) {
        datePart = Intl.DateTimeFormat(i18n.language, {
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
          timeZone: currentTimezone,
        }).format(new Date(messageDate));
      }

      if (isYesterday(messageDate)) {
        datePart = t('static.generic.yesterday');
      }

      if (isThisISOWeek(messageDate)) {
        datePart = t('static.generic.this_week_day', {
          name: Intl.DateTimeFormat(i18n.language, {
            weekday: 'long',
            timeZone: currentTimezone,
          }).format(messageDate),
        });
      } else if (isThisYear(messageDate)) {
        return Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          timeZone: currentTimezone,
        }).format(new Date(messageDate));
      } else {
        datePart = Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: currentTimezone,
        }).format(new Date(messageDate));
      }

      if (datePart !== null) {
        return `${datePart}, ${Intl.DateTimeFormat(i18n.language, {
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
          timeZone: currentTimezone,
        }).format(new Date(messageDate))}`;
      }

      return Intl.DateTimeFormat(i18n.language, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    return '-';
  }, [chatUser?.last_time_online]);

  useEffect(() => {
    if (actionModal.isOpen && onActionModalConfirm) {
      if (currentConversation) {
        if (onUnblockUser) {
          console.log('Unblock conversation', currentConversation);
          dispatch(
            unblockConversationRequest({ conversationId: currentConversation }),
          );
          setOnUnblockUser(false);
        } else if (onBlockUser) {
          console.log('Block conversation', currentConversation);
          dispatch(
            blockConversationRequest({ conversationId: currentConversation }),
          );
          setOnBlockUser(false);
        }
      }
    }
  }, [onActionModalConfirm]);

  useEffect(() => {
    if (blockConversationResponse?.data || unblockConversationResponse?.data) {
      // potential problem with user token expired
      dispatch(getRecentConversationsRequest({}));
    }
  }, [blockConversationResponse, unblockConversationResponse]);

  const onEnableNotifications = () => {
    dispatch(
      userUpdateProfilePreferenceRequest({
        type: ProfilePreferenceTypeEnum.UserPushNotification,
        value: '1',
      }),
    );
    requestPushPermission();
    dispatch(setPushPreference({ enable: true, updated: Date.now() }));
  };

  const onNewAppointmentClick = useCallback(() => {
    if (chatUser && currentUser && chatConversation) {
      const isPartner = currentUser.id === chatConversation.provider;
      if (isPartner) {
        navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE));
      } else {
        const route = toLocalizedRoute(
          ROUTES.PARTNER_PUBLIC_PROFILE_ROUTE.replace(
            ':partnerId',
            `${chatConversation.partner}`,
          ).replace(
            ':partnerName',
            formatPartnerRouteName(
              `${chatConversation.user.first_name} ${chatConversation.user.last_name}`,
            ),
          ),
        );

        navigate(route);
      }
    }
  }, [currentUser, chatUser, chatConversation, toLocalizedRoute, navigate]);

  return targetConversation ? (
    <span className="flex h-[64px] max-h-[64px] min-w-full flex-row gap-0 border-b border-solid border-slate-300 shadow-sm">
      <span className="flex min-h-full grow flex-col items-start justify-center px-2 pb-[4px]">
        <span className="flex min-h-full flex-row items-start justify-start gap-3">
          <span className="flex min-h-full flex-col items-center justify-center lg:hidden">
            <button
              id="menu-chat-conversation-back"
              key="menu-chat-conversation-back"
              aria-label="menu-chat-conversation-back"
              data-test-id="menu-chat-conversation-back"
              data-umami-event="menu-chat-conversation-back-selected"
              className="cursor-pointer rounded-full py-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              type="button"
              onClick={onBack}
            >
              <IoIosArrowBack size={26} />
            </button>
          </span>
          {chatUser && (
            <span
              id="menu-chat-user-details"
              data-test-id="menu-chat-user-details"
              data-umami-event="menu-chat-user-details-selected"
              className="flex min-h-full cursor-pointer flex-row items-center justify-center gap-3"
              role="button"
              tabIndex={0}
              onClick={chatUserDetailsModal.onOpen}
              onKeyUp={onKeyUpDefault}
            >
              <span className="relative flex min-h-[48px] min-w-[48px] flex-col items-center justify-center rounded-full bg-slate-300 text-center shadow-sm">
                <span className="flex size-full min-h-full flex-col items-center justify-center text-center text-xs font-bold capitalize">
                  {`${chatUser.first_name
                    .substring(0, 1)
                    .toUpperCase()}${chatUser.last_name
                    .substring(0, 1)
                    .toUpperCase()}`}
                </span>
                {chatUser.media?.profile_picture && (
                  <img
                    alt={`${chatUser.first_name} ${chatUser.last_name}`}
                    style={{
                      objectFit: 'cover',
                      height: '48px',
                      width: '48px',
                    }}
                    src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                      chatUser.media?.profile_picture
                    }`}
                    className="absolute left-0 top-0 size-[48px] rounded-full"
                  />
                )}
                {targetConversation.blocked && (
                  <span className="absolute left-0 top-0 flex size-[48px] flex-col items-center justify-center rounded-full bg-white/75 text-slate-400">
                    <CgBlock size={48} />
                  </span>
                )}
              </span>
              <span className="flex min-h-full grow flex-col items-start justify-start gap-0">
                <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-lg font-bold">
                  {`${chatUser.first_name} ${chatUser.last_name}`}
                </span>
                {targetConversation.blocked ? (
                  <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-xs font-bold">
                    {t('static.chat.blocked')}
                  </span>
                ) : chatUser.status === ConversationUserStatusType.Online ? (
                  <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-xs font-bold text-green-600">
                    {t('static.chat.online_label')}
                  </span>
                ) : (
                  chatUser?.last_time_online && (
                    <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-xs font-semibold text-slate-500">
                      {t('static.chat.last_seen_label')}:{' '}
                      <span className="capitalize">
                        {formatLastMessageLabel}
                      </span>
                    </span>
                  )
                )}
              </span>
            </span>
          )}
        </span>
      </span>
      {(!targetConversation.blocked || !chatUser?.blocked) && (
        <span>
          <Popover className="relative flex min-h-full flex-col items-center justify-center">
            <PopoverButton
              id="menu-chat-menu-more"
              key="menu-chat-menu-more"
              aria-label="menu-chat-menu-more"
              data-test-id="menu-chat-menu-more"
              data-umami-event="menu-chat-menu-more-selected"
              className="cursor-pointer outline-none"
            >
              <span className="flex min-h-full flex-col items-center justify-center pr-2">
                <span className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                  <IoMdMore size={24} />
                </span>
              </span>
            </PopoverButton>
            <PopoverPanel className="absolute right-0 top-12 z-30 w-[200px] divide-y divide-slate-200 overflow-hidden rounded-xl border border-solid border-slate-200 bg-white text-sm shadow-md">
              <PopoverButton
                id="chat-user-menu-new-appointment"
                key="chat-user-menu-new-appointment"
                aria-label="chat-user-menu-new-appointment"
                data-test-id="chat-user-menu-new-appointment"
                data-umami-event="chat-user-menu-new-appointment"
                className="min-w-full cursor-pointer text-left"
                onClick={onNewAppointmentClick}
              >
                <MenuItem
                  active
                  label={t('static.chat.menu_new_appointment_label')}
                  icon={MdMoreTime}
                  additionalStyle="hover:bg-green-100"
                />
              </PopoverButton>
              <PopoverButton
                id="chat-user-menu-show-details"
                key="chat-user-menu-show-details"
                aria-label="chat-user-menu-show-details"
                data-test-id="chat-user-menu-show-details"
                data-umami-event="chat-user-menu-show-details"
                className="min-w-full cursor-pointer text-left"
                onClick={chatUserDetailsModal.onOpen}
              >
                <MenuItem
                  active
                  label={t('static.chat.menu_profile_information_label')}
                  icon={MdInfoOutline}
                />
              </PopoverButton>
              {!targetConversation.blocked && (
                <PopoverButton
                  id="chat-user-menu-block-user"
                  key="chat-user-menu-block-user"
                  aria-label="chat-user-menu-block-user"
                  data-test-id="chat-user-menu-block-user"
                  data-umami-event="chat-user-menu-block-user"
                  className="min-w-full cursor-pointer text-left"
                  onClick={() => {
                    setOnBlockUser(true);
                    actionModal.onOpen({
                      action: ActionModalActionType.BlockUser,
                      message: t('static.chat.block_user_dialog_text', {
                        name: `${chatUser?.first_name} ${chatUser?.last_name}`,
                      }),
                      cancelLabel: t(
                        'static.chat.menu_block_contact_cancel_btn_label',
                      ),
                      confirmLabel: t(
                        'static.chat.menu_block_contact_confirm_btn_label',
                      ),
                      confirmLabelType: 'reject',
                    });
                  }}
                >
                  <MenuItem
                    active
                    label={t('static.chat.menu_block_contact_label')}
                    additionalStyle="hover:bg-red-100"
                    icon={CgBlock}
                    iconSize={19}
                  />
                </PopoverButton>
              )}
              {targetConversation.blocked && (
                <PopoverButton
                  id="chat-user-menu-unblock-user"
                  key="chat-user-menu-unblock-user"
                  aria-label="chat-user-menu-unblock-user"
                  data-test-id="chat-user-menu-unblock-user"
                  data-umami-event="chat-user-menu-unblock-user"
                  className="min-w-full cursor-pointer text-left"
                  onClick={() => {
                    setOnUnblockUser(true);
                    actionModal.onOpen({
                      action: ActionModalActionType.UnblockUser,
                      message: t('static.chat.unblock_user_dialog_text', {
                        name: `${chatUser?.first_name} ${chatUser?.last_name}`,
                      }),
                      cancelLabel: t(
                        'static.chat.menu_unblock_contact_cancel_btn_label',
                      ),
                      confirmLabel: t(
                        'static.chat.menu_unblock_contact_confirm_btn_label',
                      ),
                      confirmLabelType: 'accept',
                    });
                  }}
                >
                  <MenuItem
                    active
                    label={t('static.chat.menu_unblock_contact_label')}
                    icon={CgUnblock}
                    additionalStyle="hover:bg-green-100"
                    iconSize={17}
                  />
                </PopoverButton>
              )}
              {isSupported && !isPermitted && (
                <PopoverButton
                  id="chat-user-menu-enable-notifications"
                  key="chat-user-menu-enable-notifications"
                  aria-label="chat-user-menu-enable-notifications"
                  data-test-id="chat-user-menu-enable-notifications"
                  data-umami-event="chat-user-menu-enable-notifications"
                  className="min-w-full cursor-pointer text-left"
                  onClick={onEnableNotifications}
                >
                  <MenuItem
                    active
                    label={t('static.chat.menu_enable_notifications_label')}
                    additionalStyle="hover:bg-green-100"
                    icon={MdOutlineNotificationsActive}
                  />
                </PopoverButton>
              )}
            </PopoverPanel>
          </Popover>
        </span>
      )}
      <span className="mr-2 hidden lg:block">
        <MainPopupMenu />
      </span>
    </span>
  ) : null;
};

export default ChatHeader;
